import { useState, useRef, useEffect } from "react";
import { PLUSH_TYPES, LOUD_COLORS, getType, getColor, SIZES, plushFilter, savePrize } from "../prizes.js";
import PlushImg from "../PlushImg.jsx";
import { t } from "../i18n.js";
import { ensureCtx, out } from "../audio.js";
import { offerAvailable, claimReward } from "../rewards.js";

/* ==================== KLOMASKINEN – the claw machine =========================
   The claw sweeps left-right; tap to drop it. A clean hit (close to a plush's
   centre) grabs it, lifts it and carries it to the chute — the plush you see
   is the prize you get (small size). Anti-farming: 3 tries per 15 minutes,
   persisted in localStorage with a visible countdown.
================================================================================ */

const W = 360, H = 540;
const RAIL_Y = 84;               // claw rest height
const GRAB_Y = 372;              // how deep the claw dives
const PILE_Y = 388;              // plush row baseline
const CHUTE_X = 52;              // drop-off chute centre
const SWING_MIN = 84, SWING_MAX = 320;
const MAX_TRIES = 3;
const WINDOW_MS = 15 * 60 * 1000;
const TRIES_KEY = "carnival.claw";

/* ---- attempt bookkeeping (localStorage) ---- */
function loadTries() {
  try {
    const arr = JSON.parse(localStorage.getItem(TRIES_KEY) || "[]");
    const now = Date.now();
    return arr.filter((ts) => now - ts < WINDOW_MS);
  } catch { return []; }
}
function recordTry() {
  const arr = loadTries();
  arr.push(Date.now());
  try { localStorage.setItem(TRIES_KEY, JSON.stringify(arr)); } catch {}
  return arr;
}
/* bonus tries bought with a rewarded ad — separate from the free quota,
   so the 3-per-15-min schedule keeps running untouched */
const BONUS_KEY = "carnival.clawBonus";
const BONUS_PER_AD = 6;              // én video = 6 nye forsøg
function loadBonus() {
  const n = parseInt(localStorage.getItem(BONUS_KEY) || "0", 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}
function setBonus(n) {
  try { localStorage.setItem(BONUS_KEY, String(Math.max(0, n))); } catch {}
}
const isLocked = () => loadTries().length >= MAX_TRIES && loadBonus() <= 0;
const fmt = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    drop() { const c = ensure(); if (c) tone(160, c.currentTime, 0.18, "triangle", 0.1); },
    grab() { const c = ensure(); if (c) { tone(520, c.currentTime, 0.1, "square", 0.1); tone(700, c.currentTime + 0.08, 0.12, "square", 0.08); } },
    slip() { const c = ensure(); if (c) tone(180, c.currentTime, 0.3, "sawtooth", 0.1); },
    win() {
      const c = ensure(); if (!c) return;
      [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.3, "triangle", 0.14));
    },
  };
}

const randPlush = (i) => ({
  id: i,
  type: PLUSH_TYPES[Math.floor(Math.random() * PLUSH_TYPES.length)],
  color: LOUD_COLORS[Math.floor(Math.random() * LOUD_COLORS.length)],
});

export default function Claw({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing
  const [done, setDone] = useState(false);       // all tries spent → summary card
  const [adErr, setAdErr] = useState(false);     // rewarded ad failed to deliver
  const [tick, setTick] = useState(0);           // 1 Hz re-render for the countdown
  const winsRef = useRef([]);                    // prizes fished this session
  const gameRef = useRef(null);                  // live machine state (for ad-reset)

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const tries = loadTries();
  const triesLeft = Math.max(0, MAX_TRIES - tries.length);
  const bonus = loadBonus();
  const nextIn = tries.length ? tries[0] + WINDOW_MS - Date.now() : 0;

  /* rewarded ad → +1 bonusforsøg (gratis-kvoten røres ikke) */
  const watchAd = () => {
    setAdErr(false);
    claimReward("klo_forsoeg", {
      onReward: () => {
        setBonus(loadBonus() + BONUS_PER_AD);
        if (gameRef.current) { gameRef.current.doneFired = false; gameRef.current.doneT = 0; }
        setDone(false);
        setTick((n) => n + 1);
      },
      onNoReward: (reason) => { if (reason !== "dismissed") setAdErr(true); },
    });
  };

  useEffect(() => {
    const iv = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  const startGame = () => {
    setDone(false);
    winsRef.current = [];
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const g = {
      state: "swing",      // swing | drop | close | lift | carry | release | rise
      now: 0,
      clawX: W / 2,
      clawY: RAIL_Y,
      open: 1,             // 1 = open, 0 = closed
      dir: 1,
      held: null,          // grabbed plush
      target: null,
      releaseT: 0,
      popups: [],
      pile: [],
    };
    gameRef.current = g;
    if (localStorage.getItem("carnival.debug")) window.__cl = g;
    // pile: løs bunke med simpel fysik (tyngdekraft + skub) — holdes fri af
    // præmieboksen til venstre; skiverne falder på plads ved start
    for (let i = 0; i < 9; i++) {
      const row = i < 5 ? 0 : 1;
      const x = 116 + (i % 5) * 44 + (row ? 22 : 0) + (Math.random() * 10 - 5);
      g.pile.push({ ...randPlush(i), x, y: PILE_Y - row * 34 - Math.random() * 10, vx: 0, vy: 0, r: 21, gone: false });
    }

    /* ---- sizing (same pattern as the other booths) ---- */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- input: a TAP drops the claw; a drag is a scroll and passes
       through untouched (the cabinet fills the screen, so blocking
       touchstart here used to kill page scrolling entirely) ---- */
    const doDrop = () => {
      audioRef.current.unlock();
      if (g.state !== "swing") return;
      if (loadTries().length >= MAX_TRIES) {
        if (loadBonus() <= 0) return;               // locked
        setBonus(loadBonus() - 1);                  // spend an ad-bought bonus try
      } else {
        recordTry();
      }
      g.dropped = true;                              // this session actually played
      setTick((n) => n + 1);                          // refresh the HUD counter
      g.state = "drop";
      audioRef.current.drop();
    };
    let tap = null;
    const onTouchStart = (e) => {
      const t0 = e.touches[0];
      tap = { x: t0.clientX, y: t0.clientY };
    };
    const onTouchMove = (e) => {
      if (!tap) return;
      const t0 = e.touches[0];
      if (Math.abs(t0.clientX - tap.x) > 10 || Math.abs(t0.clientY - tap.y) > 10) tap = null;
    };
    const onTouchEnd = (e) => {
      if (!tap) return;
      tap = null;
      e.preventDefault();          // no ghost mousedown after a handled tap
      doDrop();
    };
    canvas.addEventListener("mousedown", doDrop);
    canvas.addEventListener("touchstart", onTouchStart, { passive: true });
    canvas.addEventListener("touchmove", onTouchMove, { passive: true });
    canvas.addEventListener("touchend", onTouchEnd);

    /* ---- machine logic ---- */
    const step = (dt) => {
      g.now += dt;

      if (g.state === "swing") {
        g.clawX += g.dir * 115 * dt;
        if (g.clawX > SWING_MAX) { g.clawX = SWING_MAX; g.dir = -1; }
        if (g.clawX < SWING_MIN) { g.clawX = SWING_MIN; g.dir = 1; }
      }

      if (g.state === "drop") {
        g.clawY += 170 * dt;
        // kloen stopper naar den MOEDER bunken (foerste skive under spidsen),
        // saa den ogsaa kan gribe skiver der ligger OVENPAA — ellers ved bunden
        let touch = false;
        for (const p of g.pile) {
          if (p.gone) continue;
          if (Math.hypot(p.x - g.clawX, p.y - (g.clawY + 24)) < p.r + 2) touch = true;
        }
        if (touch || g.clawY >= GRAB_Y) {
          g.clawY = Math.min(g.clawY, GRAB_Y);
          g.state = "close";
          g.closeT = 0;
        }
      }

      if (g.state === "close") {
        g.closeT += dt;
        g.open = Math.max(0, 1 - g.closeT / 0.28);
        if (g.closeT >= 0.32) {
          // nearest live plush decides the outcome — precision = grip.
          // Kun skiver i klohoejde taeller; chancen maales paa VANDRET
          // praecision som foer fysikken (samme svaerhedsgrad)
          let best = null, bd = 1e9;
          for (const p of g.pile) {
            if (p.gone) continue;
            if (Math.abs(p.y - (g.clawY + 22)) > 34) continue;
            const d = Math.abs(p.x - g.clawX);
            if (d < bd) { bd = d; best = p; }
          }
          if (best && bd < 26 && Math.random() < Math.max(0.25, 0.95 - bd / 30)) {
            g.held = best;
            best.gone = true;
            g.popups.push({ x: g.clawX, y: g.clawY - 30, txt: t("pop_grab"), c: "#ffd75e", t: 0 });
            audioRef.current.grab();
          } else {
            g.popups.push({ x: g.clawX, y: g.clawY - 30, txt: t("pop_slip"), c: "#cfd4d8", t: 0 });
            audioRef.current.slip();
          }
          g.state = "lift";
        }
      }

      if (g.state === "lift") {
        g.clawY -= 150 * dt;
        if (g.clawY <= RAIL_Y) {
          g.clawY = RAIL_Y;
          g.state = g.held ? "carry" : "rise";
        }
      }

      if (g.state === "carry") {
        g.clawX -= 130 * dt;
        if (g.clawX <= CHUTE_X) {
          g.clawX = CHUTE_X;
          g.state = "release";
          g.releaseT = 0;
        }
      }

      if (g.state === "release") {
        g.releaseT += dt;
        g.open = Math.min(1, g.releaseT / 0.25);
        if (g.releaseT > 0.3 && g.held) {
          // the claw opens above the chute — the plush drops, visibly
          g.falling = { p: g.held, x: CHUTE_X, y: g.clawY + 34, vy: 60 };
          g.held = null;
        }
        if (g.releaseT > 0.5) g.state = "rise";
      }

      // the dropped plush falls into the chute and lies there briefly
      if (g.falling) {
        if (!g.falling.landed) {
          g.falling.vy += 620 * dt;
          g.falling.y += g.falling.vy * dt;
          if (g.falling.y >= 408) {
            g.falling.y = 408;
            g.falling.landed = true;
            g.falling.landedT = 0;
            const p = g.falling.p;
            // no popup mid-session — wins collect quietly until all tries are spent
            const prize = savePrize({ type: p.type.id, color: p.color.id, size: "lille", game: "Klomaskinen" });
            winsRef.current.push(prize);
            audioRef.current.win();
            g.popups.push({ x: W / 2, y: 300, txt: t("c_win"), c: "#ffd75e", t: 0 });
          }
        } else {
          g.falling.landedT += dt;
          if (g.falling.landedT > 1.2) g.falling = null;
        }
      }

      if (g.state === "rise") {
        g.open = Math.min(1, g.open + dt * 3);
        g.state = "swing";
      }

      /* ---- bunke-fysik: som en rigtig klomaskine ----
         Skiverne har tyngdekraft, ligger i en blød bunke og bliver skubbet
         rundt, når kloen dykker ned imellem dem eller løfter en op. */
      const FLOOR = 414, LEFT = 104, RIGHT = W - 18;
      for (const p of g.pile) {
        if (p.gone) continue;
        p.vy += 900 * dt;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vx *= Math.pow(0.2, dt);                       // rullefriktion
        if (p.y > FLOOR - p.r) {
          p.y = FLOOR - p.r;
          if (p.vy > 0) p.vy = -p.vy * 0.22;
          if (Math.abs(p.vy) < 22) p.vy = 0;
          p.vx *= 0.92;
        }
        if (p.x < LEFT + p.r) { p.x = LEFT + p.r; p.vx = Math.abs(p.vx) * 0.5; }
        if (p.x > RIGHT - p.r) { p.x = RIGHT - p.r; p.vx = -Math.abs(p.vx) * 0.5; }
      }
      // skive-mod-skive: blød separation (de må overlappe lidt = plysbunke)
      for (let i = 0; i < g.pile.length; i++)
        for (let j = i + 1; j < g.pile.length; j++) {
          const a = g.pile[i], b = g.pile[j];
          if (a.gone || b.gone) continue;
          const dx = b.x - a.x, dy = b.y - a.y;
          const d = Math.hypot(dx, dy) || 0.01, min = a.r + b.r - 8;
          if (d < min) {
            const nx = dx / d, ny = dy / d, push = (min - d) / 2;
            a.x -= nx * push; a.y -= ny * push;
            b.x += nx * push; b.y += ny * push;
            const rel = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
            if (rel < 0) {
              const im = -rel * 0.4;
              a.vx -= nx * im; a.vy -= ny * im;
              b.vx += nx * im; b.vy += ny * im;
            }
          }
        }
      // kloen skubber skiver til side på vej ned og op igen — men ALDRIG den
      // skive, den dykker ned omkring (ellers jager kloen selv præmien væk,
      // og intet kan nogensinde fanges), og ikke mens den lukker om grebet
      if (g.state === "drop" || (g.state === "lift" && g.clawY > 240)) {
        const cx = g.clawX, cy = g.clawY + 22, cr = 15;
        let nearest = null, nd = 1e9;
        if (g.state === "drop") {
          for (const p of g.pile) {
            if (p.gone) continue;
            const d = Math.hypot(p.x - cx, p.y - cy);
            if (d < nd) { nd = d; nearest = p; }
          }
        }
        for (const p of g.pile) {
          if (p.gone || p === nearest) continue;
          const dx = p.x - cx, dy = p.y - cy;
          const d = Math.hypot(dx, dy) || 0.01;
          if (d < cr + p.r - 4) {
            const nx = dx / d, ny = dy / d;
            p.x += nx * (cr + p.r - 4 - d);
            p.y += ny * (cr + p.r - 4 - d) * 0.4;        // mest sidelæns skub
            p.vx += nx * 95;
            p.vy += ny * 30 - 12;
          }
        }
      }

      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < 1);

      // all tries spent this session and the machine is idle again → summary
      // card (later: this is where an ad can be offered for extra tries)
      if (!g.doneFired && g.dropped && g.state === "swing" && !g.held && !g.falling
          && isLocked()) {
        g.doneT = (g.doneT || 0) + dt;
        if (g.doneT > 0.9) { g.doneFired = true; setDone(true); }
      }
    };

    /* ---- drawing ---- */
    const drawPlush = (p, x, y, size = 30) => {
      // felt-skive med GLØDENDE neon-ring i tøjdyrets farve (Banana-looket);
      // canvas ctx.filter er ikke understøttet på iOS, så farven bæres af
      // ringen, og dyret vises i midten — det du fanger, er det du får
      ctx.save();
      ctx.shadowColor = p.color.css;
      ctx.shadowBlur = 14;
      ctx.beginPath(); ctx.arc(x, y, size * 0.78, 0, Math.PI * 2);
      ctx.fillStyle = p.color.css; ctx.fill();
      ctx.restore();
      ctx.beginPath(); ctx.arc(x, y, size * 0.60, 0, Math.PI * 2);
      ctx.fillStyle = "#f6edd8"; ctx.fill();
      ctx.strokeStyle = "rgba(60,30,60,0.18)"; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(x, y, size * 0.60, 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 1;
      ctx.font = `${Math.round(size * 0.78)}px sans-serif`;
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText(p.type.emoji, x, y + 1);
      ctx.textBaseline = "alphabetic";
    };

    const draw = () => {
      // guld-kabinet med neonkant (Banana-looket)
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "#c79d5c");
      grad.addColorStop(0.55, "#aa7f43");
      grad.addColorStop(1, "#8a6531");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      // børstet metal
      ctx.fillStyle = "rgba(255,255,255,0.05)";
      for (let y = 0; y < H; y += 7) ctx.fillRect(0, y, W, 2);
      // neonstribe hele vejen rundt
      const neon = ctx.createLinearGradient(0, 0, W, H);
      neon.addColorStop(0, "#ff9de2");
      neon.addColorStop(1, "#8fd0ff");
      ctx.save();
      ctx.shadowColor = "#eab6ff"; ctx.shadowBlur = 10;
      ctx.strokeStyle = neon; ctx.lineWidth = 4;
      ctx.strokeRect(6, 6, W - 12, H - 12);
      ctx.restore();
      ctx.lineWidth = 1;

      // glas-boks: nattelilla indre med stjerner og bokeh-lyskæde
      const inte = ctx.createLinearGradient(0, 46, 0, 432);
      inte.addColorStop(0, "#2c1848");
      inte.addColorStop(1, "#170d2c");
      ctx.fillStyle = inte;
      ctx.fillRect(14, 46, W - 28, 386);
      for (let i = 0; i < 42; i++) {           // stjerner (deterministiske)
        const sx = 22 + ((i * 73) % (W - 44));
        const sy = 54 + ((i * 137) % 290);
        ctx.globalAlpha = 0.18 + ((i * 29) % 10) / 22;
        ctx.fillStyle = "#fff";
        ctx.fillRect(sx, sy, 1.6, 1.6);
      }
      ctx.globalAlpha = 1;
      const bokeh = ["#ff9de2", "#8fd0ff", "#b78cff", "#8fffc9", "#ffd75e"];
      for (let i = 0; i < 11; i++) {           // sløret lyskæde i baggrunden
        const bx = 226 + 88 * Math.sin(i * 0.55);
        const by = 92 + i * 23;
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = bokeh[i % bokeh.length];
        ctx.beginPath(); ctx.arc(bx, by, 5 + (i % 3) * 2, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1;

      // præmieboks: glas med guldramme og plakette
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      ctx.fillRect(CHUTE_X - 26, 340, 52, 92);
      ctx.strokeStyle = "#e3bd6e"; ctx.lineWidth = 2.5;
      ctx.strokeRect(CHUTE_X - 26, 340, 52, 92);
      ctx.fillStyle = "#8a6531";
      ctx.fillRect(CHUTE_X - 22, 347, 44, 15);
      ctx.strokeStyle = "#f0d59a"; ctx.lineWidth = 1;
      ctx.strokeRect(CHUTE_X - 22, 347, 44, 15);
      ctx.font = "800 9px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = "#ffe9b8";
      ctx.fillText(t("prizes_one").toUpperCase(), CHUTE_X, 358);

      // pile
      for (const p of g.pile) if (!p.gone) drawPlush(p, p.x, p.y);

      // dobbelt kromskinne + løbekat
      ctx.fillStyle = "#37294f";
      ctx.fillRect(14, 46, W - 28, 16);
      for (const ry of [50, 57]) {
        const rg = ctx.createLinearGradient(0, ry, 0, ry + 3.5);
        rg.addColorStop(0, "#e9edf2"); rg.addColorStop(1, "#8d95a3");
        ctx.fillStyle = rg;
        ctx.fillRect(20, ry, W - 40, 3.5);
      }
      const tg = ctx.createLinearGradient(0, 45, 0, 64);
      tg.addColorStop(0, "#e6ebf0"); tg.addColorStop(1, "#98a1ad");
      ctx.fillStyle = tg;
      ctx.fillRect(g.clawX - 17, 45, 34, 19);
      ctx.strokeStyle = "#6d7580"; ctx.strokeRect(g.clawX - 17, 45, 34, 19);
      ctx.fillStyle = "#e3bd6e";
      ctx.fillRect(g.clawX - 17, 61, 34, 3);

      // kabel + guldkrave + kromhoved
      ctx.strokeStyle = "#c3c9d2"; ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.moveTo(g.clawX, 64); ctx.lineTo(g.clawX, g.clawY - 12); ctx.stroke();
      ctx.fillStyle = "#e3bd6e";
      ctx.fillRect(g.clawX - 8, g.clawY - 15, 16, 5);
      const hg = ctx.createLinearGradient(g.clawX - 7, 0, g.clawX + 7, 0);
      hg.addColorStop(0, "#f2f5f8"); hg.addColorStop(0.5, "#b9c1cb"); hg.addColorStop(1, "#7f8895");
      ctx.fillStyle = hg;
      ctx.fillRect(g.clawX - 7, g.clawY - 10, 14, 12);

      // kromklo: to fingre + midterpind (mørk kant + lys kerne = metal)
      const spread = 6 + g.open * 12;
      ctx.lineCap = "round";
      for (const s of [-1, 1]) {
        for (const [lw, col] of [[5.5, "#7f8895"], [3, "#e9edf2"]]) {
          ctx.lineWidth = lw; ctx.strokeStyle = col;
          ctx.beginPath();
          ctx.moveTo(g.clawX, g.clawY);
          ctx.lineTo(g.clawX + s * spread, g.clawY + 14);
          ctx.lineTo(g.clawX + s * (spread - 4), g.clawY + 26);
          ctx.stroke();
        }
      }
      for (const [lw, col] of [[4, "#7f8895"], [2, "#e9edf2"]]) {
        ctx.lineWidth = lw; ctx.strokeStyle = col;
        ctx.beginPath(); ctx.moveTo(g.clawX, g.clawY); ctx.lineTo(g.clawX, g.clawY + 20); ctx.stroke();
      }
      ctx.lineWidth = 1; ctx.lineCap = "butt";

      // held plush dangles from the claw
      if (g.held) drawPlush(g.held, g.clawX, g.clawY + 34);

      // dropped plush falling into the chute
      if (g.falling) drawPlush(g.falling.p, g.falling.x, g.falling.y);

      // glas-genskin OVER indholdet + guldramme
      const sheen = ctx.createLinearGradient(0, 46, W, 432);
      sheen.addColorStop(0, "rgba(255,255,255,0.09)");
      sheen.addColorStop(0.45, "rgba(255,255,255,0.015)");
      sheen.addColorStop(1, "rgba(255,255,255,0.06)");
      ctx.fillStyle = sheen;
      ctx.fillRect(14, 46, W - 28, 386);
      ctx.strokeStyle = "#e3bd6e"; ctx.lineWidth = 3;
      ctx.strokeRect(14, 46, W - 28, 386);
      ctx.lineWidth = 1;

      // guld-kontrolpanel
      const pg = ctx.createLinearGradient(0, 432, 0, H);
      pg.addColorStop(0, "#c79d5c"); pg.addColorStop(1, "#96703a");
      ctx.fillStyle = pg;
      ctx.fillRect(0, 432, W, H - 432);
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.fillRect(0, 432, W, 2);
      // møntindkast
      ctx.fillStyle = "#7a5a2a"; ctx.fillRect(56, 456, 24, 36);
      ctx.strokeStyle = "#f0d59a"; ctx.strokeRect(56, 456, 24, 36);
      ctx.fillStyle = "#3d2b10"; ctx.fillRect(66, 464, 4, 20);
      // display med forsøg tilbage (dot-matrix-look)
      const totalLeft = Math.max(0, MAX_TRIES - loadTries().length) + loadBonus();
      ctx.fillStyle = "#241631";
      ctx.fillRect(206, 452, 126, 26);
      ctx.strokeStyle = "#f0d59a"; ctx.strokeRect(206, 452, 126, 26);
      ctx.font = "800 13px 'Courier New', monospace";
      ctx.textAlign = "center";
      ctx.fillStyle = "#ffd7f0";
      ctx.fillText(t("c_tries", { n: totalLeft }).toUpperCase(), 269, 469);
      // blank rød knap med klo-ikon
      const locked = isLocked();
      ctx.save();
      if (!locked) { ctx.shadowColor = "#ff5a4e"; ctx.shadowBlur = 16; }
      ctx.beginPath(); ctx.arc(W / 2, 486, 30, 0, Math.PI * 2);
      ctx.fillStyle = locked ? "#6b5a5a" : "#9e1d18"; ctx.fill();
      ctx.restore();
      ctx.beginPath(); ctx.arc(W / 2, 482, 26, 0, Math.PI * 2);
      ctx.fillStyle = locked ? "#7d6a6a" : "#e0392c"; ctx.fill();
      ctx.beginPath(); ctx.ellipse(W / 2 - 7, 473, 12, 7, -0.5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.35)"; ctx.fill();
      ctx.strokeStyle = locked ? "#5a4a4a" : "#7e1512"; ctx.lineWidth = 3; ctx.lineCap = "round";
      ctx.beginPath(); ctx.moveTo(W / 2, 471); ctx.lineTo(W / 2, 483); ctx.stroke();
      for (const s of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(W / 2, 483);
        ctx.lineTo(W / 2 + s * 7, 489);
        ctx.lineTo(W / 2 + s * 5, 495);
        ctx.stroke();
      }
      ctx.lineWidth = 1; ctx.lineCap = "butt";

      // hint / lock text (mørk tekst på guldpanelet)
      ctx.font = "800 13px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = "#3d2b10";
      if (locked) {
        const arr = loadTries();
        const ms = arr.length ? arr[0] + WINDOW_MS - Date.now() : 0;
        ctx.fillText(t("c_locked", { t: fmt(ms) }), W / 2, 532);
      } else if (g.state === "swing") {
        ctx.fillText(t("c_hint"), W / 2, 532);
      }

      // popups
      for (const p of g.popups) {
        const px = Math.max(60, Math.min(W - 60, p.x));
        ctx.globalAlpha = Math.max(0, 1 - p.t);
        ctx.font = "bold 24px 'Alfa Slab One', Georgia, serif";
        ctx.lineWidth = 5; ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.strokeText(p.txt, px, p.y - p.t * 30);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, px, p.y - p.t * 30);
        ctx.globalAlpha = 1;
      }
    };

    let raf, last = performance.now();
    const loop = (tt) => {
      const dt = Math.min((tt - last) / 1000, 0.033);
      last = tt;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousedown", doDrop);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);
    };
  }, [phase]);

  /* ================= UI ================= */
  return (
    <div className={"cm-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <div className="bunting">
          {Array.from({ length: 14 }).map((_, i) => <span key={i} className={i % 2 ? "fl fl-w" : "fl"} />)}
        </div>
        <h1>{t("tile_claw").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("c_sign2")}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("c_lede")}</p>
          <ul className="rules">
            <li>{t("c_rule1")}</li>
            <li>{t("c_rule2")}</li>
            <li>{t("c_rule3")}</li>
          </ul>
          <p className="prizenote">{t("c_prizenote")}</p>
          <button className="cta" onClick={startGame}>{t("c_start")}</button>
        </main>
      )}

      {phase === "playing" && (
        <>
          <div className="scorebar">
            <span>{t("c_tries", { n: triesLeft })}</span>
            {bonus > 0 && <span className="last bonus">{t("ad_bonus", { n: bonus })}</span>}
            {triesLeft === 0 && bonus === 0 && nextIn > 0 && <span className="last">{t("c_wait", { t: fmt(nextIn) })}</span>}
          </div>
          {!done && triesLeft === 0 && bonus === 0 && offerAvailable("klo_forsoeg") && (
            <div className="adrow">
              <span>{t("ad_offer_claw")}</span>
              <button className="adbtn" onClick={watchAd}>{t("ad_watch")}</button>
            </div>
          )}
          {!done && adErr && <div className="adrow fail">{t("ad_fail")}</div>}
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {done && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{winsRef.current.length ? t("c_win") : t("c_done_zero_title")}</div>
            {winsRef.current.length > 0 ? (
              <p className="prizeline">
                <span className="winrow">
                  {winsRef.current.map((w, i) => (
                    <PlushImg key={i} className="prizeemoji" type={w.type} color={w.color} px={SIZES.lille.pick + 14} />
                  ))}
                </span>
                <br />
                {t(winsRef.current.length === 1 ? "c_done_won1" : "c_done_won", { n: winsRef.current.length })}
              </p>
            ) : (
              <p className="prizeline">{t("c_done_zero")}</p>
            )}
            {nextIn > 0 && <p className="waitline">{t("c_wait", { t: fmt(nextIn) })}</p>}
            {offerAvailable("klo_forsoeg") && (
              <>
                <p className="adline">{t("ad_offer_claw")}</p>
                <button className="cta gold" onClick={watchAd}>{t("ad_watch")}</button>
              </>
            )}
            {adErr && <p className="adline fail">{t("ad_fail")}</p>}
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.cm-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c;
  min-height:100vh; background:#2b0f26;
  background-image:radial-gradient(circle at 50% -10%, #56224b 0%, #2b0f26 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.bunting{display:flex; justify-content:center; gap:2px; margin-bottom:4px;}
.fl{width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:14px solid var(--red);}
.fl-w{border-top-color:var(--cream);}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(24px,7vw,42px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .bunting{display:none;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px); text-shadow:0 2px 0 #7e1f1a, 0 4px 8px rgba(0,0,0,.45);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.prizenote{margin:12px 0 0; font-size:12px; font-weight:800; opacity:.75; line-height:1.4;}
.cta{
  width:100%; margin-top:20px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.alt{margin-top:10px; background:#6b2a5e; box-shadow:0 4px 0 #41163a; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #41163a;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.last{background:rgba(255,255,255,.15); padding:2px 10px; border-radius:20px;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #d9b360; box-shadow:0 12px 30px rgba(0,0,0,.45);
}
.boardwrap canvas{display:block; touch-action:pan-y;}
.overlay{
  position:fixed; inset:0; background:rgba(20,6,18,.75); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.prizeline{font-size:14px; font-weight:800; margin:8px 0 12px; line-height:1.5;}
.prizeemoji{line-height:1.2; display:inline-block;}
.winrow{display:inline-flex; gap:10px; justify-content:center; margin-bottom:6px;}
.waitline{font-size:13px; font-weight:800; opacity:.7; margin:0 0 4px;}
.last.bonus{background:#f5c94e; color:#4a3305;}
.adrow{
  width:min(96%,520px); display:flex; align-items:center; justify-content:center; gap:10px;
  font-size:13px; font-weight:800; padding:4px 4px 8px; text-align:center; flex-wrap:wrap;
}
.adrow.fail{opacity:.75;}
.adbtn{
  background:linear-gradient(180deg,#ffd75e,#f0a92e); color:#4a3305; border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 12px; cursor:pointer;
  box-shadow:0 3px 0 #b07a14;
}
.adbtn:active{transform:translateY(2px); box-shadow:0 1px 0 #b07a14;}
.adline{font-size:13px; font-weight:800; margin:2px 0 10px; line-height:1.4;}
.adline.fail{opacity:.7; margin:8px 0 10px;}
.cta.gold{
  background:linear-gradient(180deg,#ffd75e,#f0a92e); color:#4a3305; box-shadow:0 4px 0 #b07a14;
  margin-top:0; font-size:16px; padding:12px;
}
.cta.gold:active{box-shadow:0 1px 0 #b07a14;}
`;
